import React, { useEffect, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { IconLogin } from '@tabler/icons-react'
import {
  Alert,
  Box,
  Button,
  Center,
  Container,
  Image,
  Space,
  Text,
  Title,
} from '@mantine/core'
import logo from '../static/orikami-logo.svg'
import * as classes from './NoPermissionsPage.module.css'
import { useTranslation } from 'react-i18next'
import { SESSION_EXPIRED_KEY } from '../services/request'

const readSessionExpired = () => {
  try {
    return sessionStorage.getItem(SESSION_EXPIRED_KEY) === `true`
  } catch {
    return false
  }
}

/**
 * Shown instead of redirecting straight to Auth0's Universal Login. The Auth0 login session
 * expires when its page is left open for too long, so the login flow must only start on an
 * explicit user action.
 */
const LoginPage = () => {
  const { loginWithRedirect, error } = useAuth0()
  const { t } = useTranslation()
  const [isRedirecting, setIsRedirecting] = useState(false)
  // Set when a failed token renewal logged the user out
  const [sessionExpired] = useState(readSessionExpired)

  useEffect(() => {
    try {
      sessionStorage.removeItem(SESSION_EXPIRED_KEY)
    } catch {
      // Nothing to clean up without storage
    }
  }, [])

  const handleLogin = () => {
    setIsRedirecting(true)
    const { pathname, search, hash } = window.location
    // Don't carry the params of a failed Auth0 callback along, they would be handled again.
    const isAuthCallback = new URLSearchParams(search).has(`state`)
    const returnTo = isAuthCallback ? pathname : `${pathname}${search}${hash}`
    loginWithRedirect({ appState: { returnTo } }).catch(() =>
      setIsRedirecting(false),
    )
  }

  return (
    <Container size="xl" m="lg">
      <Center>
        <Box className={classes.inner}>
          <Image src={logo} alt="Orikami Logo" className={classes.image} />
          <Box className={classes.content}>
            <Title>{t(`login.title`)}</Title>
            <Space h="lg" />
            <Text>{t(`login.text`)}</Text>
            {sessionExpired && !error && (
              <Alert color="yellow" mt="lg">
                {t(`login.sessionExpired`)}
              </Alert>
            )}
            {error && (
              <Alert color="red" mt="lg">
                {t(`login.error`)}
              </Alert>
            )}
            <Space h="lg" />
            <Button
              onClick={handleLogin}
              size="lg"
              loading={isRedirecting}
              leftSection={<IconLogin size={20} />}
            >
              {t(`login.button`)}
            </Button>
          </Box>
        </Box>
      </Center>
    </Container>
  )
}

export default LoginPage
